import { API_BASE } from '@/shared/services/api';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Map, Network, TreeDeciduous, Menu, ChevronRight, ChevronDown,
  Search, Copy, Check, Filter, Eye, EyeOff, Info, ExternalLink,
  Layers, FileText, Settings, Shield, Users, ChevronLeft,
  ZoomIn, ZoomOut, Maximize2, X, Home, Database, Key, Link2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/shared/components/ui/select';
import { Tooltip, InfoTooltip, TermTooltip } from '@/shared/components/ui/tooltip';
import {
  NAVIGATION_SSOT,
  NODE_TYPES,
  MODULE_OWNERS,
  PERMISSION_ROLES,
  TOOLTIP_DICTIONARY,
  getAllNodes,
  getNodeById,
  getNodeChildren,
  getBreadcrumbChain,
  getMenuNodes,
  getNodesByModule,
  getNodesByType,
  searchNodes,
  getClickPath,
  buildTree,
  buildMenuTree
} from '@/shared/data/navigationSSOT';

// Node type badge colors
const NODE_TYPE_COLORS = {
  [NODE_TYPES.PAGE]: 'bg-blue-500',
  [NODE_TYPES.SECTION]: 'bg-purple-500',
  [NODE_TYPES.GROUP]: 'bg-green-500',
  [NODE_TYPES.WIZARD]: 'bg-amber-500',
  [NODE_TYPES.MODAL]: 'bg-pink-500',
  [NODE_TYPES.EXTERNAL_LINK]: 'bg-gray-500'
};

// Module owner colors
const MODULE_COLORS = {
  [MODULE_OWNERS.ADMIN]: '#6366f1',
  [MODULE_OWNERS.ERP]: '#8b5cf6',
  [MODULE_OWNERS.EZPARCEL]: '#dc2626',
  [MODULE_OWNERS.WMS]: '#2563eb',
  [MODULE_OWNERS.FINANCE]: '#16a34a',
  [MODULE_OWNERS.PROCUREMENT]: '#4f46e5',
  [MODULE_OWNERS.CRM]: '#14b8a6',
  [MODULE_OWNERS.COMMUNICATIONS]: '#8b5cf6',
  [MODULE_OWNERS.OIG]: '#06b6d4',
  [MODULE_OWNERS.CALENDAR]: '#3b82f6',
  [MODULE_OWNERS.EZTASK]: '#f59e0b',
  [MODULE_OWNERS.ECOMMERCE]: '#10b981',
  [MODULE_OWNERS.CNS]: '#ec4899',
  [MODULE_OWNERS.REPORTS]: '#3b82f6'
};

// Detail Panel Component
const DetailPanel = ({ node, onClose }) => {
  if (!node) return null;

  const breadcrumb = getBreadcrumbChain(node.id);

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-background border-l shadow-xl z-50 overflow-y-auto">
      <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between">
        <h3 className="font-semibold">Node Details</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4 space-y-4">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className={NODE_TYPE_COLORS[node.type]}>{node.type}</Badge>
            {node.badge && <Badge variant="outline">{node.badge}</Badge>}
            {node.status === 'deprecated' && <Badge variant="destructive">Deprecated</Badge>}
          </div>
          <h2 className="text-xl font-bold">{node.title}</h2>
          <p className="text-sm text-muted-foreground">{node.route}</p>
        </div>

        {/* Breadcrumb */}
        <div>
          <label className="text-xs font-medium text-muted-foreground">Breadcrumb Path</label>
          <div className="flex items-center gap-1 text-sm flex-wrap mt-1">
            {breadcrumb.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                <span className={idx === breadcrumb.length - 1 ? 'font-medium' : ''}>{item.title}</span>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-muted-foreground">Description</label>
          <p className="text-sm mt-1">{node.description}</p>
        </div>

        {/* Key Features */}
        {node.key_features?.length > 0 && (
          <div>
            <label className="text-xs font-medium text-muted-foreground">Key Features</label>
            <ul className="list-disc list-inside text-sm mt-1 space-y-1">
              {node.key_features.map((feature, idx) => (
                <li key={idx}>{feature}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Module Owner */}
        <div>
          <label className="text-xs font-medium text-muted-foreground">Module Owner</label>
          <div className="flex items-center gap-2 mt-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: MODULE_COLORS[node.module_owner] || '#666' }}
            />
            <span className="text-sm">{node.module_owner}</span>
          </div>
        </div>

        {/* Permissions */}
        {node.permissions_required?.length > 0 && (
          <div>
            <label className="text-xs font-medium text-muted-foreground">Permissions Required</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {node.permissions_required.map(perm => (
                <Badge key={perm} variant="secondary" className="text-xs">{perm}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {node.tags?.length > 0 && (
          <div>
            <label className="text-xs font-medium text-muted-foreground">Tags</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {node.tags.map(tag => (
                <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Visibility */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            {node.visible_in_menu ? (
              <Eye className="h-4 w-4 text-green-500" />
            ) : (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            )}
            <span>Visible in Menu: {node.visible_in_menu ? 'Yes' : 'No'}</span>
          </div>
        </div>

        {/* Links To */}
        {node.links_to?.length > 0 && (
          <div>
            <label className="text-xs font-medium text-muted-foreground">Cross-Navigation Links</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {node.links_to.map(linkId => {
                const linkedNode = getNodeById(linkId);
                return linkedNode ? (
                  <Badge key={linkId} variant="outline" className="text-xs">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    {linkedNode.title}
                  </Badge>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Tab 1: Navigation Overview
const NavigationOverview = () => (
  <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Network className="h-5 w-5" />
          Navigation Architecture Patterns
        </CardTitle>
        <CardDescription>
          Understanding how EzERP navigation is structured
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Global Nav Patterns */}
        <div>
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            Global Navigation Patterns
            <InfoTooltip content="The standard navigation elements present across all pages of the application" />
          </h4>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <h5 className="font-medium flex items-center gap-2">
                <Menu className="h-4 w-4" />
                Sidebar
              </h5>
              <p className="text-sm text-muted-foreground mt-2">
                Primary navigation with module groups, collapsible sections, and quick access to all major features.
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <h5 className="font-medium flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Header Bar
              </h5>
              <p className="text-sm text-muted-foreground mt-2">
                Module switcher, search, notifications, and user menu for cross-cutting actions.
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <h5 className="font-medium flex items-center gap-2">
                <ChevronRight className="h-4 w-4" />
                Breadcrumb
              </h5>
              <p className="text-sm text-muted-foreground mt-2">
                Hierarchical path indicator showing current location and enabling quick navigation up the tree.
              </p>
            </div>
          </div>
        </div>

        {/* Grouping Principles */}
        <div>
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            Module Grouping Principles
            <InfoTooltip content={TOOLTIP_DICTIONARY['Module Owner']} />
          </h4>
          <p className="text-sm text-muted-foreground mb-3">
            Navigation is organized by functional modules, each representing a distinct business domain:
          </p>
          <div className="grid md:grid-cols-4 gap-2">
            {Object.values(MODULE_OWNERS).map(module => (
              <div key={module} className="flex items-center gap-2 p-2 border rounded">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: MODULE_COLORS[module] || '#666' }}
                />
                <span className="text-sm">{module}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RBAC Concept */}
        <div>
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <TermTooltip term="RBAC" definition={TOOLTIP_DICTIONARY['RBAC']} /> Visibility Principle
          </h4>
          <div className="bg-muted/50 p-4 rounded-lg">
            <p className="text-sm">
              <strong>No Lateral Access:</strong> Users only see navigation items they have permission to access.
              Hidden items are completely invisible – not grayed out or disabled. This ensures a clean,
              uncluttered interface and prevents confusion about inaccessible features.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.values(PERMISSION_ROLES).map(role => (
                <Badge key={role} variant="secondary">{role}</Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Node Types */}
        <div>
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <TermTooltip term="Node Types" definition={TOOLTIP_DICTIONARY['Node Type']} />
            <InfoTooltip content="Different types of navigation elements in the system" />
          </h4>
          <div className="grid md:grid-cols-3 gap-2">
            {Object.entries(NODE_TYPES).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2 p-2 border rounded">
                <Badge className={NODE_TYPE_COLORS[value]}>{value}</Badge>
                <span className="text-xs text-muted-foreground capitalize">
                  {key.toLowerCase().replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>

    {/* SSOT Explanation */}
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          About This Mapping System
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          All navigation views in this Visual Mapping feature are derived from a{' '}
          <TermTooltip term="Single Source of Truth (SSOT)" definition={TOOLTIP_DICTIONARY['SSOT']} />.
          This ensures consistency across all maps and makes maintenance straightforward –
          update the SSOT once, and all views automatically reflect the change.
        </p>
        <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm">
          <strong>SSOT Location:</strong> <code className="bg-zinc-800 text-zinc-100 px-2 py-0.5 rounded">/shared/data/navigationSSOT.js</code>
        </div>
      </CardContent>
    </Card>
  </div>
);

// Tab 2: Sitemap (IA Diagram)
const SitemapDiagram = ({ onSelectNode }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set(['root', 'ezparcel', 'wms', 'finance']));
  const [zoom, setZoom] = useState(100);
  const tree = useMemo(() => buildTree('root'), []);

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const expandAll = () => {
    const allIds = getAllNodes().map(n => n.id);
    setExpandedNodes(new Set(allIds));
  };

  const collapseAll = () => {
    setExpandedNodes(new Set(['root']));
  };

  const TreeNode = ({ node, depth = 0 }) => {
    if (!node) return null;
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.childNodes?.length > 0;

    return (
      <div className="relative">
        <div
          className={`
            flex items-center gap-2 p-2 rounded cursor-pointer
            hover:bg-muted/50 transition-colors
            ${depth === 0 ? 'font-semibold' : ''}
          `}
          style={{ paddingLeft: `${depth * 24 + 8}px` }}
          onClick={() => hasChildren ? toggleNode(node.id) : onSelectNode?.(node)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => { e.stopPropagation(); toggleNode(node.id); }}
              className="p-0.5 hover:bg-muted rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}

          <div
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: MODULE_COLORS[node.module_owner] || '#666' }}
          />

          <span className="flex-1 truncate">{node.title}</span>

          <Badge variant="outline" className="text-xs">{node.type}</Badge>

          {node.badge && (
            <Badge className="text-xs bg-primary">{node.badge}</Badge>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
            onClick={(e) => { e.stopPropagation(); onSelectNode?.(node); }}
          >
            <Info className="h-3 w-3" />
          </Button>
        </div>

        {isExpanded && hasChildren && (
          <div className="border-l border-muted ml-5">
            {node.childNodes.map(child => (
              <TreeNode key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setZoom(Math.max(50, zoom - 10))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm w-12 text-center">{zoom}%</span>
          <Button variant="outline" size="sm" onClick={() => setZoom(Math.min(150, zoom + 10))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setZoom(100)}>
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tree Diagram */}
      <Card>
        <CardContent className="pt-4 overflow-x-auto">
          <div style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}>
            {tree && <TreeNode node={tree} />}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Legend</CardTitle>
        </CardHeader>
        <CardContent className="py-2">
          <div className="flex flex-wrap gap-4">
            {Object.entries(MODULE_COLORS).slice(0, 8).map(([module, color]) => (
              <div key={module} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs">{module}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Tab 3: Navigation Hierarchy (SSOT Table)
const NavigationHierarchy = ({ onSelectNode }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');

  const filteredNodes = useMemo(() => {
    let nodes = getAllNodes();

    if (searchQuery) {
      nodes = searchNodes(searchQuery);
    }

    if (moduleFilter !== 'all') {
      nodes = nodes.filter(n => n.module_owner === moduleFilter);
    }

    if (typeFilter !== 'all') {
      nodes = nodes.filter(n => n.type === typeFilter);
    }

    if (visibilityFilter !== 'all') {
      const visible = visibilityFilter === 'visible';
      nodes = nodes.filter(n => n.visible_in_menu === visible);
    }

    return nodes.sort((a, b) => a.title.localeCompare(b.title));
  }, [searchQuery, moduleFilter, typeFilter, visibilityFilter]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search nodes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {Object.values(MODULE_OWNERS).map(module => (
                  <SelectItem key={module} value={module}>{module}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.values(NODE_TYPES).map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="visible">Menu Visible</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredNodes.length} of {getAllNodes().length} nodes
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">
                    Title
                    <InfoTooltip content="The display name of this navigation node" />
                  </th>
                  <th className="text-left p-3 font-medium">Route</th>
                  <th className="text-left p-3 font-medium">
                    <span className="flex items-center gap-1">
                      Type
                      <InfoTooltip content={TOOLTIP_DICTIONARY['Node Type']} />
                    </span>
                  </th>
                  <th className="text-left p-3 font-medium">
                    <span className="flex items-center gap-1">
                      Module
                      <InfoTooltip content={TOOLTIP_DICTIONARY['Module Owner']} />
                    </span>
                  </th>
                  <th className="text-center p-3 font-medium">
                    <span className="flex items-center justify-center gap-1">
                      Menu
                      <InfoTooltip content={TOOLTIP_DICTIONARY['Visible in Menu']} />
                    </span>
                  </th>
                  <th className="text-left p-3 font-medium">Permissions</th>
                </tr>
              </thead>
              <tbody>
                {filteredNodes.map(node => (
                  <tr
                    key={node.id}
                    className="border-b hover:bg-muted/30 cursor-pointer"
                    onClick={() => onSelectNode?.(node)}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{node.title}</span>
                        {node.badge && <Badge className="text-xs">{node.badge}</Badge>}
                      </div>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground font-mono">
                      {node.route}
                    </td>
                    <td className="p-3">
                      <Badge className={`${NODE_TYPE_COLORS[node.type]} text-xs`}>
                        {node.type}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: MODULE_COLORS[node.module_owner] || '#666' }}
                        />
                        <span className="text-sm">{node.module_owner}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      {node.visible_in_menu ? (
                        <Eye className="h-4 w-4 text-green-500 mx-auto" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground mx-auto" />
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {node.permissions_required?.slice(0, 2).map(perm => (
                          <Badge key={perm} variant="secondary" className="text-xs">{perm}</Badge>
                        ))}
                        {node.permissions_required?.length > 2 && (
                          <Badge variant="secondary" className="text-xs">+{node.permissions_required.length - 2}</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Tab 4: Menu Tree (UI)
const MenuTree = ({ onSelectNode }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set(['ezparcel', 'wms', 'finance', 'admin']));
  const menuTree = useMemo(() => buildMenuTree('root'), []);

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const MenuItem = ({ node, depth = 0 }) => {
    if (!node) return null;
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.childNodes?.length > 0;

    return (
      <div>
        <div
          className={`
            flex items-center gap-3 p-2 rounded-lg cursor-pointer
            hover:bg-muted transition-colors
            ${depth === 0 ? 'bg-muted/50' : ''}
          `}
          style={{ marginLeft: `${depth * 16}px` }}
          onClick={() => hasChildren ? toggleNode(node.id) : onSelectNode?.(node)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => { e.stopPropagation(); toggleNode(node.id); }}
              className="p-0.5"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}

          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${MODULE_COLORS[node.module_owner]}20` }}
          >
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: MODULE_COLORS[node.module_owner] || '#666' }}
            />
          </div>

          <div className="flex-1">
            <div className="font-medium text-sm">{node.title}</div>
            <div className="text-xs text-muted-foreground">{node.route}</div>
          </div>

          {node.badge && (
            <Badge className="text-xs">{node.badge}</Badge>
          )}
        </div>

        {isExpanded && hasChildren && (
          <div className="mt-1">
            {node.childNodes.map(child => (
              <MenuItem key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Menu className="h-5 w-5" />
            Sidebar Menu Preview
          </CardTitle>
          <CardDescription>
            This shows how the navigation appears in the sidebar. Only nodes with <code>visible_in_menu: true</code> are shown.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-md bg-background border rounded-lg p-2 space-y-1">
            {menuTree.map(node => (
              <MenuItem key={node.id} node={node} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Menu Statistics</CardTitle>
        </CardHeader>
        <CardContent className="py-2">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold">{menuTree.length}</div>
              <div className="text-xs text-muted-foreground">Top-level Items</div>
            </div>
            <div>
              <div className="text-2xl font-bold">
                {menuTree.reduce((sum, node) => sum + (node.childNodes?.length || 0), 0)}
              </div>
              <div className="text-xs text-muted-foreground">Sub-items</div>
            </div>
            <div>
              <div className="text-2xl font-bold">
                {getMenuNodes().length}
              </div>
              <div className="text-xs text-muted-foreground">Total Visible</div>
            </div>
            <div>
              <div className="text-2xl font-bold">
                {getAllNodes().filter(n => !n.visible_in_menu).length}
              </div>
              <div className="text-xs text-muted-foreground">Hidden Pages</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Tab 5: Breadcrumb Rules
const BreadcrumbRules = () => {
  const [selectedNodeId, setSelectedNodeId] = useState('wms-picking');
  const allNodes = useMemo(() => getAllNodes().filter(n => n.type === NODE_TYPES.PAGE), []);

  const breadcrumb = useMemo(() => getBreadcrumbChain(selectedNodeId), [selectedNodeId]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ChevronRight className="h-5 w-5" />
            Breadcrumb Trail Generator
          </CardTitle>
          <CardDescription>
            Breadcrumbs are automatically generated from the SSOT parent chain.
            Select a page to see its breadcrumb trail.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={selectedNodeId} onValueChange={setSelectedNodeId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a page" />
            </SelectTrigger>
            <SelectContent>
              {allNodes.map(node => (
                <SelectItem key={node.id} value={node.id}>
                  {node.title} ({node.route})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Breadcrumb Preview */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <label className="text-xs font-medium text-muted-foreground mb-2 block">
              Generated Breadcrumb:
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <Home className="h-4 w-4" />
              {breadcrumb.map((item, idx) => (
                <React.Fragment key={item.id}>
                  {idx > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <span className={`
                    ${idx === breadcrumb.length - 1 ? 'font-medium' : 'text-muted-foreground hover:text-foreground cursor-pointer'}
                  `}>
                    {item.title}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rules Explanation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Breadcrumb Rules</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium mb-1">1. Parent Chain Traversal</h4>
              <p className="text-sm text-muted-foreground">
                Breadcrumbs are built by following <code>menu_parent_id</code> from the current node up to the root.
              </p>
            </div>

            <div className="p-3 border rounded-lg">
              <h4 className="font-medium mb-1">2. Label Override</h4>
              <p className="text-sm text-muted-foreground">
                Nodes can specify a <code>breadcrumb_label</code> to display a different text than their title.
              </p>
            </div>

            <div className="p-3 border rounded-lg">
              <h4 className="font-medium mb-1">3. Hidden Parents</h4>
              <p className="text-sm text-muted-foreground">
                Nodes can specify <code>breadcrumb_hidden_parent_ids</code> to skip certain ancestors in the trail.
              </p>
            </div>

            <div className="p-3 border rounded-lg">
              <h4 className="font-medium mb-1">4. Root Exclusion</h4>
              <p className="text-sm text-muted-foreground">
                The root node is typically excluded from breadcrumbs and replaced with a home icon.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Tab 6: Click Paths
const ClickPaths = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const results = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchNodes(searchQuery).slice(0, 15);
  }, [searchQuery]);

  const copyPath = (nodeId, path) => {
    navigator.clipboard.writeText(path);
    setCopiedId(nodeId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Where Do I Find It?
          </CardTitle>
          <CardDescription>
            Search for a feature or page name to see the click path to reach it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search for a feature (e.g., 'picking', 'invoice', 'dashboard')..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {searchQuery && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">
              {results.length > 0 ? `Found ${results.length} results` : 'No results found'}
            </CardTitle>
          </CardHeader>
          {results.length > 0 && (
            <CardContent className="py-2 space-y-2">
              {results.map(node => {
                const path = getClickPath(node.id);
                return (
                  <div
                    key={node.id}
                    className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{node.title}</span>
                        <Badge variant="outline" className="text-xs">{node.type}</Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyPath(node.id, path)}
                      >
                        {copiedId === node.id ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded font-mono">
                      {path}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Route: <code>{node.route}</code>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          )}
        </Card>
      )}

      {/* Popular Paths */}
      {!searchQuery && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Popular Navigation Paths</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {['wms-picking', 'finance-banking', 'ezparcel-shipments', 'reports-new', 'cns-content'].map(nodeId => {
              const node = getNodeById(nodeId);
              if (!node) return null;
              const path = getClickPath(nodeId);
              return (
                <div key={nodeId} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{node.title}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyPath(nodeId, path)}
                    >
                      {copiedId === nodeId ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div className="text-sm text-muted-foreground">{path}</div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Tab 7: QA Coverage Map (Optional)
const QACoverageMap = () => {
  const allPages = useMemo(() =>
    getAllNodes().filter(n => n.type === NODE_TYPES.PAGE),
    []
  );

  // Mock coverage data - in real implementation, this would come from test results
  const coverageData = useMemo(() => {
    return allPages.map(node => ({
      ...node,
      coverage: node.status === 'active' ?
        (node.tags?.includes('daily') ? 'covered' : 'partial') :
        'unknown'
    }));
  }, [allPages]);

  const stats = useMemo(() => ({
    covered: coverageData.filter(n => n.coverage === 'covered').length,
    partial: coverageData.filter(n => n.coverage === 'partial').length,
    unknown: coverageData.filter(n => n.coverage === 'unknown').length,
    total: coverageData.length
  }), [coverageData]);

  return (
    <div className="space-y-4">
      <Card className="bg-amber-500/10 border-amber-500/30">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-amber-600">
            <Info className="h-5 w-5" />
            <span className="text-sm">
              This is a placeholder view. Actual QA coverage data would be integrated from your test suite.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-green-500">{stats.covered}</div>
            <div className="text-sm text-muted-foreground">Covered</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-amber-500">{stats.partial}</div>
            <div className="text-sm text-muted-foreground">Partial</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-gray-400">{stats.unknown}</div>
            <div className="text-sm text-muted-foreground">Unknown</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Pages</div>
          </CardContent>
        </Card>
      </div>

      {/* Coverage Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Page</th>
                  <th className="text-left p-3 font-medium">Module</th>
                  <th className="text-left p-3 font-medium">Route</th>
                  <th className="text-center p-3 font-medium">Coverage</th>
                </tr>
              </thead>
              <tbody>
                {coverageData.map(node => (
                  <tr key={node.id} className="border-b">
                    <td className="p-3 font-medium">{node.title}</td>
                    <td className="p-3 text-sm">{node.module_owner}</td>
                    <td className="p-3 text-sm font-mono text-muted-foreground">{node.route}</td>
                    <td className="p-3 text-center">
                      <Badge
                        variant={
                          node.coverage === 'covered' ? 'default' :
                            node.coverage === 'partial' ? 'secondary' :
                              'outline'
                        }
                        className={
                          node.coverage === 'covered' ? 'bg-green-500' :
                            node.coverage === 'partial' ? 'bg-amber-500' :
                              ''
                        }
                      >
                        {node.coverage}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Tab 8: Database ER Diagram
const DatabaseER = () => {
  const [selectedModule, setSelectedModule] = useState('all');
  const [expandedTables, setExpandedTables] = useState(new Set(['users', 'clients', 'businesses']));
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = API_BASE || '';

  // Fetch schema from backend API
  useEffect(() => {
    const fetchSchema = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/reference/database-schema`);
        if (!response.ok) throw new Error('Failed to fetch schema');
        const data = await response.json();
        setSchema(data);
      } catch (err) {
        console.error('Error fetching schema:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSchema();
  }, [API_URL]);

  const toggleTable = (tableName) => {
    setExpandedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableName)) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
  };

  const filteredSchema = useMemo(() => {
    if (!schema?.modules) return {};
    if (selectedModule === 'all') return schema.modules;
    return { [selectedModule]: schema.modules[selectedModule] };
  }, [selectedModule, schema]);

  // Get all table names for expand/collapse all
  const allTableNames = useMemo(() => {
    if (!schema?.modules) return [];
    const names = [];
    Object.values(schema.modules).forEach(module => {
      module.tables.forEach(table => names.push(table.name));
    });
    return names;
  }, [schema]);

  // Expand/Collapse All functions
  const expandAllTables = () => {
    setExpandedTables(new Set(allTableNames));
  };

  const collapseAllTables = () => {
    setExpandedTables(new Set());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <Database className="h-12 w-12 mx-auto mb-4 text-muted-foreground animate-pulse" />
          <p className="text-muted-foreground">Loading database schema...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <Database className="h-12 w-12 mx-auto mb-4 text-destructive" />
          <p className="text-destructive">Failed to load schema: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Entity Relationship Diagram
          </CardTitle>
          <CardDescription>
            Complete database schema for EzERP multi-tenant platform ({schema?.totals?.tables || 0} tables). All tables support tenant isolation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 flex-wrap">
            <Select value={selectedModule} onValueChange={setSelectedModule}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {schema?.modules && Object.entries(schema.modules)
                  .sort((a, b) => a[1].name.localeCompare(b[1].name))
                  .map(([key, module]) => (
                    <SelectItem key={key} value={key}>{module.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={expandAllTables}>
                Expand All
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAllTables}>
                Collapse All
              </Button>
            </div>
            <div className="flex gap-4 text-sm text-muted-foreground ml-auto">
              <span><strong>{schema?.totals?.tables || 0}</strong> Tables</span>
              <span><strong>{schema?.totals?.columns || 0}</strong> Columns</span>
              <span><strong>{schema?.totals?.foreign_keys || 0}</strong> Foreign Keys</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ER Diagram */}
      <div className="grid gap-6">
        {Object.entries(filteredSchema)
          .sort((a, b) => a[1].name.localeCompare(b[1].name))
          .map(([moduleKey, module]) => (
            <Card key={moduleKey}>
              <CardHeader className="py-3" style={{ borderLeft: `4px solid ${module.color}` }}>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: module.color }} />
                  {module.name}
                  <Badge variant="outline" className="ml-2">{module.tables.length} tables</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {module.tables.map(table => {
                    const isExpanded = expandedTables.has(table.name);
                    const fkCount = table.columns.filter(c => c.fk).length;

                    return (
                      <div
                        key={table.name}
                        className="border rounded-lg overflow-hidden bg-card hover:shadow-md transition-shadow"
                      >
                        {/* Table Header */}
                        <div
                          className="p-3 bg-muted/50 cursor-pointer flex items-center justify-between"
                          onClick={() => toggleTable(table.name)}
                        >
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            <Database className="h-4 w-4" style={{ color: module.color }} />
                            <span className="font-mono font-medium text-sm">{table.name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {fkCount > 0 && (
                              <Badge variant="outline" className="text-xs">
                                <Link2 className="h-3 w-3 mr-1" />
                                {fkCount}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Columns */}
                        {isExpanded && (
                          <div className="divide-y">
                            {table.columns.map(col => (
                              <div
                                key={col.name}
                                className={`px-3 py-1.5 text-xs flex items-center justify-between ${col.pk ? 'bg-amber-500/10' : col.fk ? 'bg-blue-500/10' : ''}`}
                              >
                                <div className="flex items-center gap-2">
                                  {col.pk && <Key className="h-3 w-3 text-amber-500" />}
                                  {col.fk && <Link2 className="h-3 w-3 text-blue-500" />}
                                  <span className={`font-mono ${col.pk ? 'font-bold' : ''}`}>{col.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground">{col.type}</span>
                                  {col.unique && <Badge variant="outline" className="text-[10px] px-1">UQ</Badge>}
                                  {col.fk && (
                                    <span className="text-blue-500 text-[10px]">→ {col.fk}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
      </div>
    </div>
  );
};

// Main Visual Mapping Component
export const VisualMapping = () => {
  const [selectedNode, setSelectedNode] = useState(null);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-3">
          <Map className="h-6 w-6 text-primary" />
          Visual Mapping
        </h2>
        <p className="text-muted-foreground mt-1">
          Explore the complete navigation structure of EzERP. All views are derived from a single source of truth.
        </p>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
          <TabsTrigger value="sitemap" className="text-xs">Sitemap</TabsTrigger>
          <TabsTrigger value="hierarchy" className="text-xs">SSOT</TabsTrigger>
          <TabsTrigger value="menu" className="text-xs">Menu Tree</TabsTrigger>
          <TabsTrigger value="breadcrumb" className="text-xs">Breadcrumbs</TabsTrigger>
          <TabsTrigger value="clickpath" className="text-xs">Click Paths</TabsTrigger>
          <TabsTrigger value="qa" className="text-xs">QA Map</TabsTrigger>
          <TabsTrigger value="database" className="text-xs">Database ER</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <NavigationOverview />
        </TabsContent>

        <TabsContent value="sitemap">
          <SitemapDiagram onSelectNode={setSelectedNode} />
        </TabsContent>

        <TabsContent value="hierarchy">
          <NavigationHierarchy onSelectNode={setSelectedNode} />
        </TabsContent>

        <TabsContent value="menu">
          <MenuTree onSelectNode={setSelectedNode} />
        </TabsContent>

        <TabsContent value="breadcrumb">
          <BreadcrumbRules />
        </TabsContent>

        <TabsContent value="clickpath">
          <ClickPaths />
        </TabsContent>

        <TabsContent value="qa">
          <QACoverageMap />
        </TabsContent>

        <TabsContent value="database">
          <DatabaseER />
        </TabsContent>
      </Tabs>

      {/* Detail Panel */}
      {selectedNode && (
        <DetailPanel node={selectedNode} onClose={() => setSelectedNode(null)} />
      )}
    </div>
  );
};

export default VisualMapping;
